import React, { useState, useEffect, useCallback, memo } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import OnetAttribution from '../../../components/OnetAttribution';
import { useAuth } from '../../../contexts/AuthContext';
import OccupationService from '../../../services/occupationService';

const OccupationSelector = memo(({ 
  selectedOccupation, 
  onOccupationSelect, 
  className = '',
  userRole: propUserRole, 
  isAuthenticated: propIsAuthenticated 
}) => {
  // Get user info from auth context if not provided as props
  const { userProfile, user, isAuthenticated: contextIsAuthenticated } = useAuth();
  const userRole = propUserRole || userProfile?.role || user?.role || 'free';
  const isAuthenticated = propIsAuthenticated !== undefined ? propIsAuthenticated : contextIsAuthenticated;

  // Debug logging (reduced)
  // console.log('OccupationSelector - User data:', {
  //   userProfile,
  //   user,
  //   userRole,
  //   isAuthenticated,
  //   propUserRole
  // });

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [allOccupations, setAllOccupations] = useState([]); // Store ALL occupations for counting
  const [occupations, setOccupations] = useState([]); // Current filtered occupations for display
  const [filteredOccupations, setFilteredOccupations] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({}); // Cache actual counts per category
  const [trueCategoryCounts, setTrueCategoryCounts] = useState({}); // True counts without user role filtering
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);

  /**
   * Load true counts for all categories without user role filtering
   */
  const loadTrueCategoryCounts = useCallback(async () => {
    try {
      // console.log('Loading true category counts...');
      
      // Get all occupations without user role filtering by using a premium user role
      const { data, error: occupationsError } = await OccupationService?.searchOccupations({
        userRole: 'premium', // Use premium role to get all occupations
        useOnet: false // Fast path: local DB only for initial counts
      });
      
      if (occupationsError) {
        console.error('OccupationSelector: True counts error:', occupationsError);
        return;
      }

      // console.log('Loaded all occupations for true counts:', data?.length || 0);
      
      // Calculate counts per category
      const counts = {};
      if (data) {
        data.forEach(occupation => {
          const categoryId = occupation.category_id;
          counts[categoryId] = (counts[categoryId] || 0) + 1;
        });
      }
      
      setTrueCategoryCounts(counts);
      // console.log('True category counts:', counts);
    } catch (err) {
      console.error('OccupationSelector: Error loading true counts:', err);
    }
  }, []);

  /**
   * Load ALL occupations initially for counting purposes
   */
  const loadAllOccupations = useCallback(async () => {
    try {
      // console.log('Loading ALL occupations for counting...');
      
      const { data, error: occupationsError } = await OccupationService?.searchOccupations({
        userRole,
        useOnet: false // Fast path: local DB only for initial full list
        // Remove limit to get all occupations
      });
      
      if (occupationsError) {
        console.error('OccupationSelector: All occupations error:', occupationsError);
        return;
      }

      // console.log('Loaded all occupations:', data?.length || 0);
      setAllOccupations(data || []);
    } catch (err) {
      console.error('OccupationSelector: Error loading all occupations:', err);
    }
  }, [userRole]);

  /**
   * Load occupations based on current filters
   */
  const loadOccupations = useCallback(async () => {
    try {
      setLoading(true);
      
      const categoryId = selectedCategory !== 'all' ? selectedCategory : null;
      
      const { data, error: occupationsError } = await OccupationService?.searchOccupations({
        categoryId,
        userRole,
        useOnet: false // Fast path: local DB only for initial category view
        // Remove limit to get all occupations
      });
      
      if (occupationsError) {
        console.error('OccupationSelector: Occupations error:', occupationsError);
        setError('Failed to load occupations. Please try again.');
        return;
      }

      console.log('loadOccupations: Loaded data for category', selectedCategory, ':', {
        totalCount: data?.length || 0,
        sampleOccupation: data?.[0] ? {
          title: data[0].title,
          category_id: data[0].category_id,
          category: data[0].category,
          category_name: data[0].category_name,
          salary_range_min: data[0].salary_range_min,
          salary_range_max: data[0].salary_range_max
        } : null
      });
      
      // Debug: Check for salary data
      const occupationsWithSalary = data?.filter(occ => occ.salary_range_min && occ.salary_range_max) || [];
      console.log(`Found ${occupationsWithSalary.length} occupations with salary data out of ${data?.length || 0} total`);
      
      // CLIENT-SIDE FILTERING: Ensure only occupations matching the selected category are shown
      let filteredData = data || [];
      
      if (selectedCategory !== 'all' && filteredData.length > 0) {
        const originalCount = filteredData.length;
        
        // Get the actual category ID for the selected category slug
        // Check if selectedCategory is already a UUID (category ID) or a slug
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCategory);
        const selectedCategoryId = isUuid ? selectedCategory : categories?.find(cat => cat.slug === selectedCategory)?.id;
        
        // Debug: Show category mapping
        console.log('Category filtering debug:', {
          selectedCategory,
          isUuid,
          selectedCategoryId,
          availableCategories: categories?.map(c => ({ slug: c.slug, id: c.id, name: c.name }))
        });
        
        // Only filter if we have the category ID, otherwise show all data
        if (selectedCategoryId) {
          filteredData = filteredData.filter(occupation => {
            // STRICT FILTERING: Only show jobs that match the exact category_id
            const matchesCategoryId = occupation?.category_id === selectedCategoryId;
            
            // Debug logging for mismatched jobs
            if (!matchesCategoryId && occupation?.category_id) {
              console.log('Filtering out job with wrong category:', {
                title: occupation.title,
                jobCategoryId: occupation.category_id,
                selectedCategoryId: selectedCategoryId,
                jobCategoryName: occupation.category_name
              });
            }
            
            return matchesCategoryId;
          });
          
          console.log(`LoadOccupations client-side filtering: ${originalCount} -> ${filteredData.length} occupations for category ${selectedCategory} (ID: ${selectedCategoryId})`);
          
          // Debug: Show sample of filtered occupations
          console.log('Sample filtered occupations after strict filtering:', filteredData.slice(0, 5).map(o => ({
            title: o.title,
            category_id: o.category_id,
            category_name: o.category_name,
            source: o.source
          })));
          
          if (filteredData.length !== originalCount) {
            console.warn('LoadOccupations API returned incorrect data! Filtering client-side to fix categorization.');
            console.log('Sample filtered occupations:', filteredData.slice(0, 3).map(o => ({
              title: o.title,
              category_id: o.category_id,
              category: o.category,
              category_name: o.category_name
            })));
          }
        } else {
          console.warn(`LoadOccupations: Category ID not found for slug: ${selectedCategory}. Showing all data.`);
        }
      }
      
      // Apply free plan limits (approx 75 total, proportional by category)
      if (userRole === 'free') {
        if (selectedCategory !== 'all') {
          const limit = memoizedCategoryCounts[selectedCategory] ?? FREE_TARGET_TOTAL;
          filteredData = filteredData.slice(0, Math.max(0, limit));
        } else {
          const allowedMap = {};
          categories.forEach(c => { allowedMap[c.id] = memoizedCategoryCounts[c.id] || 0; });
          const target = FREE_TARGET_TOTAL;
          const picked = [];
          for (const job of filteredData) {
            const cid = job?.category_id;
            if (allowedMap[cid] > 0) {
              picked.push(job);
              allowedMap[cid] -= 1;
              if (picked.length >= target) break;
            }
          }
          // If we still have room due to small categories not covering target, just stop at picked length (approximate target)
          filteredData = picked;
        }
      }
 
      console.log('FINAL FILTERED DATA:', {
        originalCount: data?.length || 0,
        filteredCount: filteredData.length,
        selectedCategory,
        sampleJobs: filteredData.slice(0, 3).map(job => ({
          title: job.title,
          category_id: job.category_id,
          category_name: job.category_name
        }))
      });
      
      setOccupations(filteredData);
      setFilteredOccupations(filteredData || []);
      
      // Cache the actual count for this category
      if (selectedCategory !== 'all') {
        setCategoryCounts(prev => ({
          ...prev,
          [selectedCategory]: filteredData.length
        }));
        console.log(`Cached count for category ${selectedCategory}:`, filteredData.length);
      }
    } catch (err) {
      console.error('OccupationSelector: Error loading occupations:', err);
      setError('Unable to load occupations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, userRole, categories]);

  /**
   * Perform search with current parameters
   */
  const performSearch = useCallback(async () => {
    try {
      setSearchLoading(true);
      
      const categoryId = selectedCategory !== 'all' ? selectedCategory : null;

      const { data, error } = await OccupationService.searchOccupations({
        search: searchTerm || undefined,
        categoryId: categoryId,
        userRole: userRole,
        useOnet: true // Use O*NET for all users
      });

      if (error) {
        console.error('Search error:', error);
        setSearchError('Failed to search occupations. Please try again.');
        return;
      }

      // CLIENT-SIDE FILTERING: Ensure only occupations matching the selected category are shown
      let filteredData = data || [];
      
      if (selectedCategory !== 'all' && filteredData.length > 0) {
        const originalCount = filteredData.length;
        
        // Get the actual category ID for the selected category slug
        // Check if selectedCategory is already a UUID (category ID) or a slug
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCategory);
        const selectedCategoryId = isUuid ? selectedCategory : categories?.find(cat => cat.slug === selectedCategory)?.id;
        
        // Debug: Show category mapping
        console.log('Category filtering debug:', {
          selectedCategory,
          isUuid,
          selectedCategoryId,
          availableCategories: categories?.map(c => ({ slug: c.slug, id: c.id, name: c.name }))
        });
        
        // Only filter if we have the category ID, otherwise show all data
        if (selectedCategoryId) {
          filteredData = filteredData.filter(occupation => {
            // STRICT FILTERING: Only show jobs that match the exact category_id
            return occupation?.category_id === selectedCategoryId;
          });
          
          console.log(`Search client-side filtering: ${originalCount} -> ${filteredData.length} occupations for category ${selectedCategory} (ID: ${selectedCategoryId})`);
          
          if (filteredData.length !== originalCount) {
            console.warn('Search API returned incorrect data! Filtering client-side to fix categorization.');
          }
        } else {
          console.warn(`Category ID not found for slug: ${selectedCategory}. Showing all data.`);
        }
      }
      
      // Apply free plan limits (approx 75 total, proportional by category) during search
      if (userRole === 'free') {
        if (selectedCategory !== 'all') {
          const limit = memoizedCategoryCounts[selectedCategory] ?? FREE_TARGET_TOTAL;
          filteredData = filteredData.slice(0, Math.max(0, limit));
        } else {
          const allowedMap = {};
          categories.forEach(c => { allowedMap[c.id] = memoizedCategoryCounts[c.id] || 0; });
          const target = FREE_TARGET_TOTAL;
          const picked = [];
          for (const job of filteredData) {
            const cid = job?.category_id;
            if (allowedMap[cid] > 0) {
              picked.push(job);
              allowedMap[cid] -= 1;
              if (picked.length >= target) break;
            }
          }
          filteredData = picked;
        }
      }
 
      setFilteredOccupations(filteredData);
      setSearchError(null);
    } catch (err) {
      console.error('Search exception:', err);
      setSearchError('An unexpected error occurred. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm, selectedCategory, userRole, categories]);

  /**
   * Memoized category counts to prevent infinite loops
   */
  const [memoizedCategoryCounts, setMemoizedCategoryCounts] = useState({});
  
  // Free plan target and proportional allocation helper
  const FREE_TARGET_TOTAL = 75;
  
  const computeFreeCategoryAllocations = useCallback((categoriesList, trueCountsMap, targetTotal) => {
    if (!Array.isArray(categoriesList) || categoriesList.length === 0) return { all: 0 };
    const categoryIds = categoriesList.map(c => c.id);
    const totals = categoryIds.map(id => trueCountsMap[id] || 0);
    const grandTotal = totals.reduce((a, b) => a + b, 0);
    
    // If no totals yet, do rough even split up to target
    if (!grandTotal) {
      const alloc = {};
      const base = Math.max(1, Math.floor(targetTotal / Math.max(1, categoryIds.length)));
      let sum = 0;
      for (const id of categoryIds) {
        const give = Math.min(base, Math.max(0, targetTotal - sum));
        alloc[id] = give;
        sum += give;
        if (sum >= targetTotal) break;
      }
      alloc.all = Object.values(alloc).reduce((a, b) => a + b, 0);
      return alloc;
    }
    
    // Always fully include small categories (<= 3)
    const smallKeep = new Set(categoryIds.filter(id => (trueCountsMap[id] || 0) > 0 && (trueCountsMap[id] || 0) <= 3));
    const smallKeepSum = Array.from(smallKeep).reduce((sum, id) => sum + (trueCountsMap[id] || 0), 0);

    // Remaining target after including small categories
    const remainingTarget = Math.max(0, targetTotal - smallKeepSum);

    // Largest remainder method with caps per category for the remaining categories
    const allocatableIds = categoryIds.filter(id => !smallKeep.has(id));
    const allocatableTotal = allocatableIds.reduce((sum, id) => sum + (trueCountsMap[id] || 0), 0) || 1;
    const rawShares = allocatableIds.map(id => ((trueCountsMap[id] || 0) / allocatableTotal) * remainingTarget);
    const floors = rawShares.map(x => Math.floor(x));
    const remainders = rawShares.map((x, i) => ({ id: allocatableIds[i], r: x - floors[i] }));
    const allocation = {};
    // Start by fully including small categories
    smallKeep.forEach(id => { allocation[id] = trueCountsMap[id] || 0; });
    // Apply floors for remaining categories
    for (let i = 0; i < allocatableIds.length; i++) {
      const id = allocatableIds[i];
      allocation[id] = Math.min(floors[i], (trueCountsMap[id] || 0));
    }
    let allocated = Object.values(allocation).reduce((a, b) => a + b, 0);
    let remaining = Math.max(0, targetTotal - allocated);
    
    remainders.sort((a, b) => b.r - a.r).forEach(({ id }) => {
      if (remaining <= 0) return;
      const available = (trueCountsMap[id] || 0) - allocation[id];
      if (available > 0) {
        allocation[id] += 1;
        remaining -= 1;
      }
    });
    
    if (remaining > 0) {
      for (const id of categoryIds) {
        if (remaining <= 0) break;
        const available = (trueCountsMap[id] || 0) - allocation[id];
        if (available > 0) {
          allocation[id] += 1;
          remaining -= 1;
        }
      }
    }
    
    allocation.all = Object.values(allocation).reduce((a, b) => a + b, 0);
    return allocation;
  }, []);

  /**
   * Count occupations per category - show appropriate counts based on user tier
   */
  const getCategoryOccupationCount = useCallback((categoryId) => {
    // For free users, prefer memoized allocated counts
    if (userRole === 'free') {
      if (categoryId === 'all') {
        if (memoizedCategoryCounts['all'] !== undefined) return memoizedCategoryCounts['all'];
      } else if (memoizedCategoryCounts[categoryId] !== undefined) {
        return memoizedCategoryCounts[categoryId];
      }
    }

    // For 'all' category (non-free), compute as sum of per-category counts
    if (categoryId === 'all') {
      if (!categories?.length) return 0;
      return categories.reduce((sum, c) => sum + getCategoryOccupationCount(c.id), 0);
    }

    if (allOccupations && categories) {
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        const selectedCategoryId = category.id;
        const categorySlug = category.slug;

        const validCategoryOccupations = allOccupations.filter(occupation => {
          const matchesId = occupation?.category_id === selectedCategoryId;
          const matchesSlug = occupation?.category_id && categorySlug && occupation.category_id === categorySlug;
          return matchesId || matchesSlug;
        });

        return validCategoryOccupations.length;
      }
    }

    if (memoizedCategoryCounts[categoryId] !== undefined) {
      return memoizedCategoryCounts[categoryId];
    }
    return 0;
  }, [memoizedCategoryCounts, allOccupations, categories, userRole]);

  /**
   * Load occupation categories from the database
   */
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: categoriesError } = await OccupationService?.getCategories();
      
      if (categoriesError) {
        console.error('OccupationSelector: Categories error:', categoriesError);
        setError('Failed to load occupation categories. Please refresh and try again.');
        return;
      }

      console.log('loadCategories: Loaded categories:', data?.map(c => ({
        id: c.id,
        name: c.name,
        slug: c.slug
      })));
      
      setCategories(data || []);
    } catch (err) {
      console.error('OccupationSelector: Error loading categories:', err);
      setError('Unable to connect to occupation database. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Load true counts for all categories
  useEffect(() => {
    loadTrueCategoryCounts();
  }, [loadTrueCategoryCounts]);

  // Load ALL occupations for counting (not filtered by category)
  useEffect(() => {
    loadAllOccupations();
  }, [loadAllOccupations]);

  // Load initial occupations (filtered by selected category)
  useEffect(() => {
    if (categories?.length > 0) {
      loadOccupations();
    }
  }, [categories, loadOccupations]);

  // Update memoized counts when data changes
  useEffect(() => {
    if (categories?.length > 0) {
      const newCounts = {};
      
      // For free users, use predefined limits
      if (userRole === 'free') {
        const alloc = computeFreeCategoryAllocations(categories, trueCategoryCounts, FREE_TARGET_TOTAL);
        categories.forEach(category => {
          newCounts[category.id] = alloc[category.id] || 0;
        });
        newCounts['all'] = FREE_TARGET_TOTAL; // Display target total for 'All'
      } else {
        // For premium users, use true counts
        categories.forEach(category => {
          newCounts[category.id] = trueCategoryCounts[category.id] || 0;
        });
        newCounts['all'] = Object.values(trueCategoryCounts).reduce((sum, count) => sum + count, 0);
      }
      
      setMemoizedCategoryCounts(newCounts);
    }
  }, [categories, trueCategoryCounts, userRole]);

  // Handle search with debouncing
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm || selectedCategory !== 'all') {
        performSearch();
      } else {
        setFilteredOccupations(occupations || []);
      }
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, selectedCategory, occupations, performSearch]);

  /**
   * Handle occupation selection with premium access check and deselection support
   */
  const handleOccupationSelect = (occupation) => {
    // Check if user can access this occupation
    if (occupation?.tier === 'premium' && !OccupationService?.canAccessPremiumOccupations(userRole)) {
      setShowPremiumUpgrade(true);
      return;
    }

    // If the same occupation is already selected, deselect it (pass null)
    if (selectedOccupation?.id === occupation?.id) {
      onOccupationSelect?.(null);
    } else {
      onOccupationSelect?.(occupation);
    }
  };

  /**
   * Get category information by ID
   */
  const getCategoryInfo = (categoryName) => {
    if (!categoryName) return null;
    return categories?.find(cat => cat?.name === categoryName);
  };

  /**
   * Handle category selection
   */
  const handleCategorySelect = (categoryId) => {
    console.log('handleCategorySelect: Selecting category:', categoryId);
    console.log('Available categories:', categories.map(c => ({ id: c.id, name: c.name })));
    setSelectedCategory(categoryId);
    setSearchTerm(''); // Clear search when changing categories
    setSearchError(null); // Clear search error when changing categories
    setShowPremiumUpgrade(false);
  };

  /**
   * Get occupation count based on user tier
   */
  const getOccupationCount = () => {
    if (!categories?.length) return allOccupations?.length || 0;
    return categories.reduce((sum, c) => sum + getCategoryOccupationCount(c.id), 0);
  };

  // Compute display total as (true total - 20) per business request
  const getAdjustedTotalCount = () => {
    const total = getOccupationCount();
    return Math.max(0, (total || 0) - 20);
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Select Your Occupation</h2>
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading occupations...</p>
          </div>
          
          {/* Enhanced loading message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-blue-800 font-medium text-sm mb-1">Loading occupations</p>
                <p className="text-blue-700 text-xs">
                  Our comprehensive database contains detailed occupation information. 
                  This may take a moment to load completely.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Select Your Occupation</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-red-700">
              <Icon name="AlertCircle" size={20} />
              <p className="font-medium">Error Loading Occupations</p>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button 
              onClick={() => {
                setError(null);
                loadCategories();
              }}
              variant="outline"
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-foreground">Select Your Occupation</h2>
        <p className="text-muted-foreground">
          {userRole === 'free'
            ? 'Choose from approximately 75 occupations to receive personalized career development recommendations'
            : `Choose from over ${getAdjustedTotalCount()}+ occupations to receive personalized career development recommendations`}
        </p>
        {/* User Tier Indicator */}
        {userRole === 'free' && (
          <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
            <Icon name="User" size={14} />
            <span>Free: Approximately 75 O*NET occupations with salary estimates</span>
          </div>
        )}
        {(userRole === 'premium' || userRole === 'admin') && (
          <div className="inline-flex items-center space-x-2 bg-warning/10 text-warning px-3 py-1 rounded-full text-sm">
            <Icon name="Crown" size={14} className="text-warning" />
            <span>Over {getAdjustedTotalCount()} jobs, fully mapped with O*NET and BLS data for smarter decisions.</span>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto relative bg-white p-4 rounded-lg border-2 border-primary/20 shadow-sm">
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Icon name="Search" size={18} className="text-primary" />
          </div>
          <Input
            type="search"
            placeholder="🔍 Search by job title, skills, or industry..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e?.target?.value);
              setSearchError(null); // Clear search error when typing
            }}
            className="w-full pl-12 pr-10 text-base py-3 border-2 border-primary/30 focus:border-primary"
          />
        {searchLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
        {searchLoading && (
          <div className="absolute -bottom-8 left-0 right-0 text-center">
            <p className="text-xs text-muted-foreground">Searching through {getAdjustedTotalCount()}+ occupations...</p>
          </div>
        )}
        </div>
      </div>

      {/* Search Error Display */}
      {searchError && (
        <div className="max-w-md mx-auto mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <Icon name="AlertCircle" size={16} color="#dc2626" className="mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Search Error</h3>
              <p className="text-sm text-red-700 mt-1">{searchError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Category Filters */}
      <div className="flex flex-wrap justify-center gap-2">
        <button
          onClick={() => handleCategorySelect('all')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            selectedCategory === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <span>All Categories</span>
          <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
            {getCategoryOccupationCount('all')}
          </span>
        </button>
        {categories?.sort((a, b) => {
          const countA = getCategoryOccupationCount(a.id);
          const countB = getCategoryOccupationCount(b.id);
          
          // First sort by count (highest first)
          if (countB !== countA) {
            return countB - countA;
          }
          
          // If counts are equal, sort alphabetically by name
          return a.name.localeCompare(b.name);
        })?.map((category) => (
          <button
            key={category?.id}
            onClick={() => handleCategorySelect(category?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              selectedCategory === category?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            <span className="hidden sm:inline">{category?.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              selectedCategory === category?.id
                ? 'bg-white/20 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}>
              {getCategoryOccupationCount(category?.id)}
            </span>
          </button>
        ))}
      </div>

      {/* Premium Upgrade Prompt */}
      {showPremiumUpgrade && userRole === 'free' && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Crown" size={24} className="text-warning mt-1" />
            <div className="flex-1">
              <h3 className="font-medium text-warning mb-1">
                Unlock Premium Occupations
              </h3>
              <p className="text-purple-700 text-sm mb-3">
                Access all occupations with detailed O*NET data, salary insights, and career progression paths.
              </p>
              <div className="space-x-2">
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                  Upgrade to Premium
                </Button>
                {!isAuthenticated && (
                  <Button size="sm" variant="outline" className="text-purple-600 border-purple-200">
                    Sign Up Free
                  </Button>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowPremiumUpgrade(false)}
              className="text-purple-500 hover:text-purple-700"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Occupation Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        {filteredOccupations?.sort((a, b) => a.title.localeCompare(b.title))?.map((occupation) => {
          const categoryInfo = getCategoryInfo(occupation?.category_name);
          const isSelected = selectedOccupation?.id === occupation?.id;
          const isPremium = occupation?.tier === 'premium';
          const canAccess = !isPremium || OccupationService?.canAccessPremiumOccupations(userRole);
          
          return (
            <button
              key={occupation?.id}
              onClick={() => handleOccupationSelect(occupation)}
              disabled={!canAccess}
              className={`p-4 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-md relative ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-md hover:bg-primary/10'
                  : canAccess
                  ? 'border-border bg-card hover:border-primary/50' :'border-border bg-muted/50 opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Selection indicator circle in top-right corner */}
              <div className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                isSelected && canAccess
                  ? 'bg-green-500 border-green-500'
                  : 'bg-white border-gray-300'
              }`} />
              
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${occupation?.category_bg_color || 'bg-gray-50'}`}>
                  <Icon 
                    name={occupation?.category_icon || 'Briefcase'} 
                    size={20} 
                    className={occupation?.category_color || 'text-gray-600'} 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className={`font-medium text-sm ${
                      isSelected ? 'text-primary' : 'text-foreground'
                    }`}>
                      {occupation?.title}
                    </h3>
                    {isPremium && (
                      <Icon name="Crown" size={14} className="text-warning flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {occupation?.description}
                  </p>
                  <div className="flex items-center flex-wrap gap-1 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      occupation?.category_bg_color || 'bg-gray-50'
                    } ${occupation?.category_color || 'text-gray-600'}`}>
                      {occupation?.category_name}
                    </span>
                    {occupation?.salary_range_min && occupation?.salary_range_max && (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-600">
                        ${Math.round(occupation?.salary_range_min / 1000)}k-${Math.round(occupation?.salary_range_max / 1000)}k
                      </span>
                    )}
                    {!canAccess && (
                      <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-600">
                        Premium
                      </span>
                    )}
                  </div>
                  {occupation?.tags?.slice(0, 2)?.map(tag => (
                    <span key={tag} className="inline-block text-xs bg-slate-100 text-slate-600 px-1 py-0.5 rounded mt-1 mr-1">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* No Results State */}
      {filteredOccupations?.length === 0 && !searchLoading && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No occupations found</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm 
              ? `Try adjusting your search terms or selecting a different category`
              : userRole === 'free' 
                ? `This category contains specialized occupations available to Premium users`
                : `No occupations available in this category`
            }
          </p>
          {searchTerm && (
            <Button 
              variant="outline" 
              onClick={() => setSearchTerm('')}
            >
              Clear Search
            </Button>
          )}
          {userRole === 'free' && (
            <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Crown" size={20} className="text-warning mt-1 flex-shrink-0" />
                <div className="text-left">
                  <h4 className="font-medium text-warning mb-1">Unlock Premium Occupations</h4>
                  <p className="text-muted-foreground text-sm">
                    This category contains specialized roles available to Premium users. 
                    Upgrade to access all occupations across all categories.
                  </p>
                  <Button 
                    size="sm" 
                    className="mt-2 bg-warning hover:bg-warning/90 text-white"
                  >
                    Upgrade to Premium
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results Summary */}
      {filteredOccupations?.length > 0 && (
        <div className="text-center text-sm text-muted-foreground">
          {searchTerm ? (
            <>
              Found {filteredOccupations?.length} occupation{filteredOccupations?.length !== 1 ? 's' : ''} matching "{searchTerm}"
              {selectedCategory !== 'all' && (
                <> in {categories?.find(c => c?.id === selectedCategory)?.name}</>
              )}
            </>
          ) : (
            <>
              {selectedCategory !== 'all' && (
                <>Showing {getCategoryOccupationCount(selectedCategory)} jobs within {categories?.find(c => c?.id === selectedCategory)?.name}</>
              )}
            </>
          )}
          {userRole === 'free' && (
            <div className="mt-2">
              <span className="text-purple-600 font-medium">Upgrade to Premium to see all occupations</span>
            </div>
          )}
        </div>
      )}

      {/* O*NET Attribution */}
      <OnetAttribution />
    </div>
  );
});

OccupationSelector.displayName = 'OccupationSelector';

export default OccupationSelector;